import {useId, useRef, useState, type CSSProperties, type DragEvent, type PointerEvent} from 'react';
import {MathText} from './MathText';
import {
  checkTitrationLabeling,
  encodeTitrationLabelingAnswer,
  parseTitrationLabelingAnswer,
  type TitrationLabelingAnswer,
  type TitrationLabelingConfig,
  type TitrationLabelSlot,
} from './titration-labeling';
import './titration-labeling.css';

const positions: Record<TitrationLabelSlot, {x:number;y:number;path:string;point:[number,number];badge:[number,number]}> = {
  burette: {x:10,y:65,path:'M250 108 H285 L330 148',point:[330,148],badge:[306,143]},
  titrant: {x:570,y:110,path:'M570 153 H425 L347 255',point:[347,255],badge:[411,255]},
  tap: {x:10,y:255,path:'M250 298 H290 L348 348',point:[348,348],badge:[384,350]},
  support: {x:570,y:270,path:'M570 313 H445 L180 282',point:[180,282],badge:[204,282]},
  vessel: {x:10,y:365,path:'M250 408 H275 L293 494',point:[293,494],badge:[257,488]},
  sample: {x:570,y:405,path:'M570 448 H455 L380 530',point:[380,530],badge:[416,516]},
  stirbar: {x:10,y:510,path:'M250 553 H290 L335 542',point:[335,542],badge:[298,536]},
  stirrer: {x:570,y:550,path:'M570 593 H450 L405 582',point:[405,582],badge:[430,582]},
};

/** The colourless titrant and amber sample are deliberately drawn separately from the glass walls. */
export function ColorimetricTitrationDrawing({slots, compact=false}: {slots:TitrationLabelSlot[];compact?:boolean}) {
  const id = useId().replace(/:/g,'');
  return <svg className="titration-apparatus" viewBox={compact?'150 25 300 605':'0 0 820 660'} role="img" aria-labelledby={`${id}-title ${id}-desc`}>
    <title id={`${id}-title`}>Montage de titrage colorimétrique à légender</title>
    <desc id={`${id}-desc`}>Une burette graduée sur un support verse une solution dans un Erlenmeyer posé sur un agitateur magnétique. Les repères numérotés correspondent aux emplacements des étiquettes.</desc>
    <defs>
      <linearGradient id={`${id}-glass`} x1="0" x2="1"><stop stopColor="#ffffff"/><stop offset=".55" stopColor="#f7fafc"/><stop offset="1" stopColor="#e5edf2"/></linearGradient>
      <linearGradient id={`${id}-sample`} x1="0" x2="1"><stop stopColor="#f5c75b"/><stop offset="1" stopColor="#d9971b"/></linearGradient>
      <marker id={`${id}-arrow`} markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto"><path d="M0 0 L6 3 L0 6" fill="none" stroke="#546477" strokeWidth="1.25"/></marker>
      <clipPath id={`${id}-flask`}><path d="M319 412 V444 L274 530 Q265 552 284 554 H400 Q419 552 410 530 L365 444 V412 Z"/></clipPath>
    </defs>
    <g strokeLinecap="round" strokeLinejoin="round">
      <path d="M180 56 V610" stroke="#334152" strokeWidth="7"/>
      <rect x="151" y="607" width="296" height="12" rx="4" fill="#334152"/>
      <path d="M181 185 H328 M321 177 H369 M321 194 H369" fill="none" stroke="#566578" strokeWidth="6"/>
      <path d="M330 51 V331 Q330 340 340 340 H350 Q360 340 360 331 V51" fill={`url(#${id}-glass)`} stroke="#43566c" strokeWidth="2.5"/>
      <path d="M333 110 Q345 115 357 110 V330 Q357 337 350 337 H340 Q333 337 333 330 Z" fill="#eaf2f5"/>
      <path d="M326 51 Q345 56 364 51 M333 110 Q345 115 357 110" fill="none" stroke="#43566c" strokeWidth="2"/>
      {Array.from({length:26},(_,n)=><g key={n}><path d={`M${n%5===0?346:351} ${110+n*8.4} H360`} stroke="#43566c" strokeWidth={n%5===0?1.6:1}/>{n%5===0&&<text x="367" y={114+n*8.4} fill="#42556b" fontSize="12" stroke="none">{n}</text>}</g>)}
      <path d="M340 340 V360 L345 381 L350 360 V340" fill="#edf4f7" stroke="#43566c" strokeWidth="2"/>
      <rect x="333" y="344" width="24" height="9" rx="3" fill="#d6e0e7" stroke="#43566c" strokeWidth="2"/>
      <path d="M349 348 H374 M374 342 V354" stroke="#43566c" strokeWidth="3"/>
      <path d="M345 391 Q339 399 345 403 Q351 399 345 391" fill="#e6eff4" stroke="#7b95a8" strokeWidth="1.3"/>
      <path d="M319 412 V444 L274 530 Q265 552 284 554 H400 Q419 552 410 530 L365 444 V412" fill={`url(#${id}-glass)`} stroke="#43566c" strokeWidth="2.5"/>
      <g clipPath={`url(#${id}-flask)`}><rect x="266" y="524" width="153" height="32" fill={`url(#${id}-sample)`}/><path d="M275 524 Q343 530 410 524" fill="none" stroke="#b77a0e" strokeWidth="1.6"/></g>
      <path d="M315 412 H369 M319 444 L274 530 Q265 552 284 554 H400 Q419 552 410 530 L365 444" fill="none" stroke="#43566c" strokeWidth="2.5"/>
      <path d="M326 424 V444 L286 529" fill="none" stroke="#ffffff" strokeWidth="3.5"/>
      <rect x="324" y="538" width="39" height="8" rx="4" fill="#fff" stroke="#4b6176" strokeWidth="1.8"/>
      <path d="M342 539 V545" stroke="#a4b2c1" strokeWidth="1.2"/>
      <rect x="266" y="560" width="153" height="44" rx="5" fill="#e4ebf2" stroke="#43566c" strokeWidth="2.5"/>
      <path d="M272 565 H413" stroke="#fff" strokeWidth="2"/>
      <circle cx="389" cy="584" r="11" fill="#fff" stroke="#43566c" strokeWidth="2"/>
      <path d="M389 584 L394 578" stroke="#43566c" strokeWidth="2.5"/>
      <circle cx="286" cy="584" r="4" fill="#5f8e85"/>
      {slots.map((slot,index)=>{
        const p=positions[slot];
        return compact?<g key={slot}><path d={`M${p.badge[0]} ${p.badge[1]} L${p.point[0]} ${p.point[1]}`} stroke="#546477" strokeWidth="1.6"/><circle cx={p.badge[0]} cy={p.badge[1]} r="12" fill="#fff" stroke="#37698a" strokeWidth="1.7"/><text x={p.badge[0]} y={p.badge[1]+4} textAnchor="middle" fontSize="12" fontWeight="700" fill="#244e6c">{index+1}</text></g>:<path key={slot} d={p.path} fill="none" stroke="#546477" strokeWidth="1.65" markerEnd={`url(#${id}-arrow)`}/>;
      })}
    </g>
  </svg>;
}

type PointerDrag = {id:string;x:number;y:number;moved:boolean};

export function ColorimetricTitrationDiagram({config,value,onChange,disabled=false,staticMode=false,correctionMode=false,hideTitle=false}: {
  config:TitrationLabelingConfig;
  value:string;
  onChange:(value:string)=>void;
  disabled?:boolean;
  staticMode?:boolean;
  correctionMode?:boolean;
  hideTitle?:boolean;
}) {
  const container = useRef<HTMLDivElement>(null);
  const drag = useRef<PointerDrag|null>(null);
  const ignoreClickUntil = useRef(0);
  const [selected,setSelected] = useState<string|null>(null);
  const [ghost,setGhost] = useState<{id:string;x:number;y:number}|null>(null);
  const id = useId();
  const answer = parseTitrationLabelingAnswer(value);
  const result = checkTitrationLabeling(config,answer);
  const locked = disabled || staticMode;
  const placed = config.slots.filter(slot=>Boolean(answer.placements[slot])).length;
  const labelText = (labelId:string|undefined) => config.labels.find(label=>label.id===labelId)?.text ?? '';
  const selectedText = labelText(selected??undefined);
  function change(next:TitrationLabelingAnswer) {if(!locked)onChange(encodeTitrationLabelingAnswer(next));}
  function place(slot:TitrationLabelSlot,labelId:string) {
    if(locked||!config.slots.includes(slot)||!config.labels.some(label=>label.id===labelId))return;
    const placements={...answer.placements};
    const previous=placements[slot];
    const origin=config.slots.find(candidate=>placements[candidate]===labelId);
    if(origin)delete placements[origin];
    if(previous&&origin&&origin!==slot)placements[origin]=previous;
    placements[slot]=labelId;
    change({...answer,placements,validated:false});setSelected(null);
  }
  function remove(slot:TitrationLabelSlot) {
    if(locked)return;
    const placements={...answer.placements};delete placements[slot];
    change({...answer,placements,validated:false});setSelected(null);
  }
  function choose(labelId:string) {if(!locked&&Date.now()>=ignoreClickUntil.current)setSelected(selected===labelId?null:labelId);}
  function startDrag(event:DragEvent<HTMLButtonElement>,labelId:string) {
    if(locked){event.preventDefault();return;}
    event.dataTransfer.setData('text/plain',labelId);event.dataTransfer.effectAllowed='move';setSelected(labelId);
  }
  function pointerStart(event:PointerEvent<HTMLButtonElement>,labelId:string) {
    if(locked||event.pointerType==='mouse')return;
    drag.current={id:labelId,x:event.clientX,y:event.clientY,moved:false};
    event.currentTarget.setPointerCapture(event.pointerId);
  }
  function pointerMove(event:PointerEvent<HTMLButtonElement>) {
    const current=drag.current;if(!current)return;
    if(Math.hypot(event.clientX-current.x,event.clientY-current.y)>7)current.moved=true;
    if(current.moved){setSelected(current.id);setGhost({id:current.id,x:event.clientX,y:event.clientY});}
  }
  function pointerEnd(event:PointerEvent<HTMLButtonElement>) {
    const current=drag.current;drag.current=null;setGhost(null);if(!current)return;
    if(current.moved){
      ignoreClickUntil.current=Date.now()+400;
      const target=document.elementFromPoint(event.clientX,event.clientY)?.closest<HTMLElement>('[data-label-slot]');
      if(target&&container.current?.contains(target))place(target.dataset.labelSlot as TitrationLabelSlot,current.id);
    }
  }
  function dragProps(labelId:string) {
    return {draggable:!locked,onDragStart:(event:DragEvent<HTMLButtonElement>)=>startDrag(event,labelId),onDragEnd:()=>setGhost(null),
      onPointerDown:(event:PointerEvent<HTMLButtonElement>)=>pointerStart(event,labelId),onPointerMove:pointerMove,onPointerUp:pointerEnd,
      onPointerCancel:()=>{drag.current=null;setGhost(null);}};
  }
  function slotView(slot:TitrationLabelSlot,index:number,mobile=false) {
    const assigned=answer.placements[slot];
    const wrong=answer.validated&&result.incorrectSlots.includes(slot);
    const checked=answer.validated&&!wrong&&!!assigned;
    const location=positions[slot];
    const style:CSSProperties|undefined=mobile?undefined:{left:`${location.x/8.2}%`,top:`${location.y/6.6}%`};
    return <div key={slot} className={`titration-label-slot${wrong?' is-incorrect':checked?' is-correct':''}${selected?' can-place':''}`} style={style} data-label-slot={slot}
      onDragOver={event=>{if(!locked){event.preventDefault();event.dataTransfer.dropEffect='move';}}}
      onDrop={event=>{event.preventDefault();place(slot,event.dataTransfer.getData('text/plain'));}}>
      <span className="titration-slot-number" aria-hidden="true">{index+1}</span>
      {staticMode?<div className="titration-slot-content"><MathText text={labelText(assigned)||'Non légendé'}/></div>:<button type="button"
        className="titration-slot-content" disabled={disabled}
        aria-label={`Repère ${index+1} : ${labelText(assigned)||'vide'}${selected?' ; placer l’étiquette sélectionnée':''}`}
        aria-pressed={!!assigned&&selected===assigned}
        {...(assigned?dragProps(assigned):{})}
        onClick={()=>{if(Date.now()<ignoreClickUntil.current)return;if(selected)place(slot,selected);else if(assigned)choose(assigned);}}>
        <MathText text={labelText(assigned)||(selected?'Placer ici':'Déposer une étiquette')}/>
      </button>}
      {assigned&&!locked&&<button type="button" className="titration-remove-label" aria-label={`Retirer l’étiquette du repère ${index+1}`} onClick={()=>remove(slot)}>×</button>}
      {(checked||wrong)&&<span className="titration-slot-result" aria-label={wrong?'Placement à reprendre':'Placement correct'}>{wrong?'!':'✓'}</span>}
    </div>;
  }
  return <div ref={container} className={`titration-labeling${staticMode?' is-static':''}${correctionMode?' is-correction':''}`}>
    {(config.title||correctionMode)&&<p className="titration-labeling-title">{correctionMode?'Montage légendé':config.title}</p>}
    {!staticMode&&<>
      <p className="titration-instructions" id={`${id}-instructions`}>Déplace chaque étiquette vers le repère correspondant. Tu peux aussi sélectionner une étiquette, puis cliquer sur son emplacement. Au clavier, utilise Tab et Entrée.</p>
      <div className="titration-label-bank" aria-label="Étiquettes à placer" aria-describedby={`${id}-instructions`}>
        {config.labels.filter(label=>!Object.values(answer.placements).includes(label.id)).map(label=><button key={label.id} type="button" className={`titration-label-chip${selected===label.id?' is-selected':''}`}
          disabled={disabled} aria-pressed={selected===label.id} {...dragProps(label.id)} onClick={()=>choose(label.id)}><MathText text={label.text}/></button>)}
        {placed===config.slots.length&&<p>Toutes les étiquettes sont placées. Tu peux encore les déplacer ou les retirer.</p>}
      </div>
      <p className="titration-selection" role="status">{selectedText?<><strong>Étiquette sélectionnée : </strong><MathText text={selectedText}/></>:`${placed} / ${config.slots.length} étiquettes placées`}</p>
    </>}
    <div className="titration-desktop-stage"><ColorimetricTitrationDrawing slots={config.slots}/>{config.slots.map((slot,index)=>slotView(slot,index))}</div>
    <div className="titration-mobile-stage"><ColorimetricTitrationDrawing slots={config.slots} compact/><div className="titration-mobile-slots">{config.slots.map((slot,index)=>slotView(slot,index,true))}</div></div>
    {!hideTitle&&<div className="titration-answer-title">{staticMode?<p><strong>Titre du schéma : </strong><MathText text={answer.title||'Non renseigné'}/></p>:<label><span>Titre du schéma{config.requireTitle?' *':''}</span><input value={answer.title} disabled={disabled} maxLength={240} placeholder="Donne un titre précis au montage" onChange={event=>change({...answer,title:event.target.value,validated:false})}/></label>}</div>}
    {!staticMode&&<div className="titration-labeling-actions">
      <button type="button" disabled={disabled||!result.complete} onClick={()=>{change({...answer,validated:true});setSelected(null);}}>Valider les étiquettes</button>
      <span>{!result.complete?(placed<config.slots.length?'Place toutes les étiquettes pour vérifier.':'Ajoute un titre pour vérifier le montage.'):'Tu peux vérifier puis reprendre les placements.'}</span>
    </div>}
    {answer.validated&&<p role="status" className={`titration-labeling-feedback ${result.correct?'is-correct':'is-incorrect'}`}>
      <strong>{result.correct?'Les étiquettes sont correctement placées.':`${result.correctCount} / ${result.total} étiquettes bien placées.`}</strong>{result.correct?' Le titre reste à vérifier par le professeur.':' Reprends les repères signalés, puis valide de nouveau.'}
    </p>}
    {ghost&&<div className="titration-drag-ghost" style={{left:ghost.x+12,top:ghost.y-20}}><MathText text={labelText(ghost.id)}/></div>}
  </div>;
}
